import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { buscarPerfisDoUsuario, retornarPerfisUsuario } from '../services/profileService';
import { CurrentProfile, ProfileOption } from '../types/profile';
import ProfileSelectionModal from '../components/ProfileSelectionModal';
import { navigate } from '../routes/navigation';
import { isTokenExpired } from '../utils/tokenUtils';

interface ProfileContextProps {
  currentProfile: CurrentProfile | null;
  availableProfiles: ProfileOption[];
  setProfile: (profile: CurrentProfile | null) => void;
  refreshProfiles: () => Promise<void>;
  loadProfiles: () => Promise<ProfileOption[]>;
  isLoading: boolean;
  isSelectionModalOpen: boolean;
  isSelectionModalForced: boolean;
  openSelectionModal: (options?: { force?: boolean }) => void;
  closeSelectionModal: () => void;
}

const ProfileContext = createContext<ProfileContextProps | null>(null);
const STORAGE_KEY = 'currentProfile';

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();
  const token = auth?.token ?? null;
  const user = auth?.user ?? null;
  const [currentProfile, setCurrentProfile] = useState<CurrentProfile | null>(null);
  const [availableProfiles, setAvailableProfiles] = useState<ProfileOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectionModalOpen, setSelectionModalOpen] = useState(false);
  const [isSelectionModalForced, setSelectionModalForced] = useState(false);
  const hasLoadedFromStorage = useRef(false);
  const currentProfileRef = useRef<CurrentProfile | null>(null);
  const userRole = (user as { role?: string } | null)?.role;
  const isAdmin = userRole === 'ADMIN';

  const applyProfile = useCallback((profile: CurrentProfile | null) => {
    currentProfileRef.current = profile;
    setCurrentProfile(profile);

    if (typeof window === 'undefined') {
      return;
    }

    if (profile) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const loadProfiles = useCallback(async (): Promise<ProfileOption[]> => {
    if (!token || !(user as { id?: string })?.id) {
      setAvailableProfiles([]);
      applyProfile(null);
      hasLoadedFromStorage.current = false;
      return [];
    }

    setIsLoading(true);
    try {
      let perfis = retornarPerfisUsuario(user as any);
      
      if (perfis.length === 0) {
        perfis = await buscarPerfisDoUsuario((user as { id: string }).id);
      }
      
      setAvailableProfiles(perfis);

      const userRole = (user as { role?: string })?.role;

      if (userRole === 'ADMIN') {
        applyProfile(null);
        hasLoadedFromStorage.current = true;
        return perfis;
      }

      const temOrganizadorAtivo = perfis.some((p) => p.tipo === 'LIGA');

      // Tenta carregar do localStorage apenas na primeira vez
      if (!hasLoadedFromStorage.current && typeof window !== 'undefined') {
        hasLoadedFromStorage.current = true;
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          try {
            const storedProfile = JSON.parse(stored) as CurrentProfile;
            const disponivel = perfis.some(
              (p) => p.tipo === storedProfile.tipo && p.referenciaId === storedProfile.referenciaId,
            );

            if (disponivel) {
              applyProfile(storedProfile);
              return perfis; // Perfil restaurado do storage, não precisa continuar
            } else {
              localStorage.removeItem(STORAGE_KEY);
            }
          } catch {
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      }

      // Verifica e atualiza o perfil atual baseado nos perfis disponíveis
      // Usa a ref para acessar o valor atual sem causar loop infinito
      const prevProfile = currentProfileRef.current;

      if (prevProfile) {
        // Se já temos um perfil selecionado, verifica se ainda está disponível
        const aindaDisponivel = perfis.some(
          (p) => p.tipo === prevProfile.tipo && p.referenciaId === prevProfile.referenciaId,
        );

        if (!aindaDisponivel) {
          applyProfile(null);
        }
      } else {
        // Se não há perfil selecionado e não há organizador ativo, seleciona atleta automaticamente
        if (!temOrganizadorAtivo) {
          const atletaPerfil = perfis.find((p) => p.tipo === 'ATLETA');
          if (atletaPerfil) {
            applyProfile({
              tipo: atletaPerfil.tipo,
              referenciaId: atletaPerfil.referenciaId,
              nome: atletaPerfil.nome,
              imagemUrl: atletaPerfil.imagemUrl,
            });
          }
        }
      }
      return perfis;
    } catch (error: any) {
      console.error('Erro ao carregar perfis:', error);
      
      // Se for erro de acesso negado, limpa os perfis silenciosamente
      if (error.message?.includes('Access Denied') || error.message?.includes('Acesso negado')) {
        setAvailableProfiles([]);
        applyProfile(null);
        hasLoadedFromStorage.current = false;
        return []; // Não relança o erro para evitar quebrar a aplicação
      }
      
      setAvailableProfiles([]);
      applyProfile(null);
      hasLoadedFromStorage.current = false;
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [token, user, applyProfile]);

  // Sincroniza a ref com o estado atual
  useEffect(() => {
    currentProfileRef.current = currentProfile;
  }, [currentProfile]);

  useEffect(() => {
    loadProfiles();
  }, [token, loadProfiles]);

  const setProfile = useCallback((profile: CurrentProfile | null) => {
    applyProfile(profile);
  }, [applyProfile]);

  const refreshProfiles = useCallback(async () => {
    hasLoadedFromStorage.current = false;
    await loadProfiles();
  }, [loadProfiles]);

  const openSelectionModal = useCallback((options?: { force?: boolean }) => {
    setSelectionModalOpen(true);
    setSelectionModalForced(options?.force ?? false);
  }, []);

  const closeSelectionModal = useCallback(() => {
    if (isSelectionModalForced) {
      return;
    }
    setSelectionModalOpen(false);
  }, [isSelectionModalForced]);

  const handleConfirmProfile = useCallback((profile: CurrentProfile) => {
    applyProfile(profile);
    
    // Redirecionar baseado no tipo de perfil
    if (profile.tipo === 'ATLETA') {
      navigate(`/userpage/${profile.referenciaId}`);
    } else if (profile.tipo === 'LIGA') {
      navigate('/liga/dashboard');
    }
    
    if (!isSelectionModalForced) {
      setSelectionModalOpen(false);
    }
  }, [applyProfile, isSelectionModalForced]);

  const mustForceSelection = !isAdmin && availableProfiles.some((p) => p.tipo === 'LIGA') && !currentProfile && !isTokenExpired(token);

  useEffect(() => {
    if (mustForceSelection) {
      setSelectionModalOpen(true);
      setSelectionModalForced(true);
    } else if (isSelectionModalForced) {
      setSelectionModalForced(false);
      setSelectionModalOpen(false);
    }
  }, [mustForceSelection, isSelectionModalForced]);

  // Limpar perfil ao fazer logout
  useEffect(() => {
    if (!token) {
      applyProfile(null);
      setAvailableProfiles([]);
      hasLoadedFromStorage.current = false;
    }
  }, [token, applyProfile]);

  return (
    <ProfileContext.Provider
      value={{
        currentProfile,
        availableProfiles,
        setProfile,
        refreshProfiles,
        loadProfiles,
        isLoading,
        isSelectionModalOpen,
        isSelectionModalForced,
        openSelectionModal,
        closeSelectionModal,
      }}
    >
      {children}
      <ProfileSelectionModal
        open={isSelectionModalOpen}
        enforceSelection={isSelectionModalForced}
        availableProfiles={availableProfiles}
        currentProfile={currentProfile}
        isLoading={isLoading}
        onRefresh={refreshProfiles}
        onConfirm={handleConfirmProfile}
        onClose={closeSelectionModal}
      />
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile deve ser usado dentro de ProfileProvider');
  }
  return context;
};

